import { useLanguage } from "@/i18n/LanguageContext";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Activity, Gauge, Wrench, Sigma, ClipboardCheck, Eye, AlertTriangle, Zap, Boxes,
  Timer, Bell, LineChart, Users, Factory, ShieldCheck, ClipboardList, Recycle,
  Search, ListFilter, ListChecks, Target, Lock, ArrowRight, Layers,
} from "lucide-react";

/* -------------------------------------------------- */
/* 1. Continuous Improvement Foundation               */
/* -------------------------------------------------- */

const ciCards = [
  {
    icon: Recycle,
    title: "Lean Manufacturing",
    body: "Identify and reduce waste across production, material flow, quality, maintenance and energy. BEEDIGIT helps factories detect waiting time, downtime, overproduction, unnecessary movement, rework, scrap, speed losses and hidden bottlenecks.",
    tags: ["Muda", "Mura", "Muri", "Waste elimination", "Flow", "Takt time", "Bottleneck", "Value stream", "Visual management"],
  },
  {
    icon: Activity,
    title: "Kaizen / Continuous Improvement",
    body: "Give improvement teams real-time facts for daily Kaizen. Track the baseline, test countermeasures, measure impact and standardize successful improvements.",
    tags: ["Kaizen", "PDCA", "Gemba", "A3 thinking", "Root cause analysis", "Countermeasures", "Standardization"],
  },
  {
    icon: Wrench,
    title: "TPM — Total Productive Maintenance",
    body: "Connect machine losses with maintenance actions. Track machine hours, downtime reasons, failures, micro-stops, preventive maintenance and autonomous maintenance activities.",
    tags: ["TPM", "Autonomous maintenance", "Planned maintenance", "Availability", "MTBF", "MTTR", "Breakdown elimination"],
  },
  {
    icon: Sigma,
    title: "Six Sigma",
    body: "Support Six Sigma projects with reliable production data for problem definition, measurement, analysis, improvement and control.",
    tags: ["DMAIC", "CTQ", "Variation", "Defects", "First-pass yield", "Pareto", "Control charts", "Cp", "Cpk"],
  },
  {
    icon: Eye,
    title: "Daily Management System",
    body: "Make daily production meetings factual and fast. Use live dashboards for shift handovers, tier meetings, SQCDP boards, Andon escalations and action tracking.",
    tags: ["DMS", "Tier meetings", "SQCDP", "Shift handover", "Action tracker", "Visual factory", "Andon", "Escalation"],
  },
  {
    icon: ClipboardCheck,
    title: "Standard Work & Operator Discipline",
    body: "Digitize job instructions, quality checks, confirmations, material consumption and escalation rules so every shift works according to the same standard.",
    tags: ["Standard Work", "Work instructions", "Checklists", "Operator confirmations", "5S", "Visual control", "Process discipline"],
  },
];

export function ContinuousImprovementFoundation() {
  const { t } = useLanguage();
  return (
    <section className="py-16 md:py-20 border-t border-border bg-[hsl(var(--section-alt))]">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mb-10">
          <p className="eyebrow mb-3">{t("Lean · Kaizen · TPM · Six Sigma")}</p>
          <h2 className="text-3xl md:text-4xl font-bold mb-4">{t("Continuous improvement starts with trusted real-time data.")}</h2>
          <p className="text-muted-foreground mb-3">
            {t("Lean and Six Sigma cannot work properly if teams are improving yesterday&apos;s data, incomplete reports or subjective opinions. BEEDIGIT gives production, maintenance, quality, energy and management teams one real-time source of truth for daily improvement.")}
          </p>
          <p className="text-muted-foreground">
            {t("Every Kaizen, TPM or Six Sigma project starts with the same question: where are we losing performance? BEEDIGIT answers this question automatically by collecting reliable data directly from machines, operators, job orders, materials, quality checks, maintenance events and energy meters. Instead of waiting for manual Excel reports, your team can see losses as they happen, classify root causes, prioritize the biggest opportunities and measure improvement results shift by shift.")}
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {ciCards.map(({ icon: Icon, title, body, tags }) => (
            <div key={title} className="rounded-lg border border-border bg-card p-5 hover-glow flex flex-col">
              <Icon className="h-6 w-6 text-primary mb-3" />
              <h3 className="font-heading font-bold text-lg mb-2">{t(title)}</h3>
              <p className="text-sm text-muted-foreground mb-3 leading-relaxed">{t(body)}</p>
              <div className="mt-auto flex flex-wrap gap-1.5">
                {tags.map((tag) => <span key={tag} className="pill pill-dot text-[11px]">{t(tag)}</span>)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------- */
/* 2. Lean Waste Table                                */
/* -------------------------------------------------- */

const wasteRows: [string, string, string][] = [
  ["Waiting", "Operators wait for material, tools, maintenance, quality approval or next job.", "Andon calls, job order visibility, material flow tracking, escalation, real-time status."],
  ["Downtime", "Machines stop without clear reasons or stops are recorded too late.", "Automatic run/stop tracking, downtime reason classification, OEE dashboards, maintenance tasks."],
  ["Micro-stops", "Small stops are ignored but destroy performance during the shift.", "Machine signal collection, short-stop detection, speed loss analysis, Pareto of repeated losses."],
  ["Overproduction", "Production continues without real demand or wrong priorities.", "Job order control, ERP/APS integration, production status, real-time output tracking."],
  ["Defects and rework", "Quality issues are found late or not linked to the real production context.", "Digital quality checks, nonconformance tracking, batch genealogy, operator/machine/order traceability."],
  ["Excess inventory and WIP", "Too much material waits between operations and no one sees exact WIP status.", "Material flow control, QR coding, batch tracking, WIP visibility, consumption confirmation."],
  ["Unnecessary motion", "Operators walk to collect papers, find instructions, ask supervisors or search materials.", "BEEPAD tablets, digital job orders, QR scanning, digital instructions, paperless confirmations."],
  ["Underused people knowledge", "Operators know the problems, but improvement ideas are not connected to data.", "Reason codes, comments, issue logging, Kaizen action tracking, dashboards for improvement teams."],
  ["Energy waste", "Machines consume energy during idle time, slow running or inefficient setup.", "Energy per machine, order, batch, meter, kg or product; idle energy analysis; ISO 50001 support."],
  ["Planning instability", "Schedules change because real capacity and real progress are not visible.", "Real-time job order status, actual output, machine availability, ERP/APS feedback."],
];

export function LeanWasteTable() {
  const { t } = useLanguage();
  return (
    <section className="py-16 md:py-20 border-t border-border">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mb-8">
          <p className="eyebrow mb-3">{t("Make waste visible before it becomes cost")}</p>
          <h2 className="text-3xl md:text-4xl font-bold mb-3">{t("See the waste. Remove the waste. Measure the result.")}</h2>
          <p className="text-muted-foreground">
            {t("BEEDIGIT helps manufacturers detect the real losses hidden inside daily production.")}
          </p>
        </div>
        <div className="overflow-x-auto rounded-lg border border-border bg-card">
          <table className="w-full text-sm">
            <thead className="bg-[hsl(var(--section-alt))] text-foreground">
              <tr>
                <th className="text-left font-semibold p-4 border-b border-border w-1/5">{t("Lean waste / loss")}</th>
                <th className="text-left font-semibold p-4 border-b border-border w-2/5">{t("How it appears in the factory")}</th>
                <th className="text-left font-semibold p-4 border-b border-border w-2/5">{t("How BEEDIGIT helps")}</th>
              </tr>
            </thead>
            <tbody>
              {wasteRows.map(([w, ex, help], i) => (
                <tr key={w} className={i % 2 ? "bg-background/30" : ""}>
                  <td className="align-top p-4 border-b border-border font-semibold text-foreground">{t(w)}</td>
                  <td className="align-top p-4 border-b border-border text-muted-foreground">{t(ex)}</td>
                  <td className="align-top p-4 border-b border-border text-muted-foreground">{t(help)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------- */
/* 3. From Data to Kaizen Action                      */
/* -------------------------------------------------- */

const kaizenSteps = [
  { icon: Activity, title: "Detect", body: "Collect real-time data from machines, operators, materials, quality, maintenance and energy." },
  { icon: ListFilter, title: "Classify", body: "Classify downtime reasons, defects, scrap, rework, maintenance issues, material delays and energy losses." },
  { icon: Target, title: "Prioritize", body: "Use Pareto analysis to focus on the biggest losses by machine, line, shift, order, product or team." },
  { icon: Search, title: "Analyze", body: "Use 5 Why, Fishbone/Ishikawa, A3 and Six Sigma DMAIC workflows to understand the root cause." },
  { icon: ClipboardList, title: "Act", body: "Assign countermeasures, maintenance actions, quality actions, operator training or process changes." },
  { icon: Layers, title: "Standardize", body: "Turn successful improvements into standard work, digital checklists and shop-floor instructions." },
  { icon: ShieldCheck, title: "Control", body: "Monitor OEE, downtime, output, quality, energy and maintenance KPIs to make sure the improvement stays." },
];

export function DataToKaizenFlow() {
  const { t } = useLanguage();
  return (
    <section className="py-16 md:py-20 border-t border-border bg-[hsl(var(--section-alt))]">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mb-10">
          <p className="eyebrow mb-3">{t("From downtime reason to Kaizen action")}</p>
          <h2 className="text-3xl md:text-4xl font-bold mb-3">{t("From machine signal to Kaizen action.")}</h2>
          <p className="text-muted-foreground">
            {t("BEEDIGIT creates a closed loop between real production events and continuous improvement.")}
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-7 gap-3">
          {kaizenSteps.map(({ icon: Icon, title, body }, i) => (
            <div key={title} className="rounded-lg border border-border bg-card p-4 relative">
              <div className="flex items-center gap-2 mb-2">
                <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-primary/15 text-primary text-xs font-bold">{i + 1}</span>
                <Icon className="h-4 w-4 text-primary" />
              </div>
              <h3 className="font-heading font-bold mb-1">{t(title)}</h3>
              <p className="text-xs text-muted-foreground leading-relaxed">{t(body)}</p>
            </div>
          ))}
        </div>
        <p className="text-center text-sm text-muted-foreground mt-8 italic">
          {t("Continuous improvement becomes measurable when every improvement action is connected to a real production baseline.")}
        </p>
      </div>
    </section>
  );
}

/* -------------------------------------------------- */
/* 4. Lean & Six Sigma Toolkit                        */
/* -------------------------------------------------- */

const toolkit = [
  { icon: Gauge, title: "Real-time OEE", use: "Availability, performance, quality, bottleneck analysis, TPM, daily production meetings." },
  { icon: LineChart, title: "Downtime Pareto", use: "Loss prioritization, Kaizen focus, root cause analysis, maintenance improvement." },
  { icon: Timer, title: "Micro-stop Analysis", use: "Performance loss detection, repeated small losses, operator and machine improvement." },
  { icon: Bell, title: "Digital Andon", use: "Fast escalation, response time tracking, maintenance/material/quality support." },
  { icon: ClipboardCheck, title: "Digital Standard Work", use: "Operator instructions, checklists, quality gates, process discipline." },
  { icon: ClipboardList, title: "Quality Data Collection", use: "Defect tracking, first-pass yield, nonconformance, rework, scrap, Six Sigma projects." },
  { icon: Boxes, title: "Material Flow Visibility", use: "WIP reduction, batch traceability, waiting time reduction, flow improvement." },
  { icon: Wrench, title: "CMMS / TPM", use: "Preventive maintenance, autonomous maintenance, MTBF, MTTR, machine history." },
  { icon: Zap, title: "Energy Loss Analysis", use: "ISO 50001, energy per unit, idle energy, energy cost reduction." },
  { icon: Eye, title: "Visual Management Dashboards", use: "Tier meetings, shift handovers, SQCDP boards, management reviews." },
  { icon: ListChecks, title: "Action Tracking", use: "PDCA, A3, Kaizen actions, countermeasures, accountability." },
  { icon: Factory, title: "ERP / APS Feedback", use: "Better planning, realistic capacity, production confirmation, closed-loop operations." },
];

export function LeanSixSigmaToolkit() {
  const { t } = useLanguage();
  return (
    <section className="py-16 md:py-20 border-t border-border">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mb-10">
          <p className="eyebrow mb-3">{t("Toolkit")}</p>
          <h2 className="text-3xl md:text-4xl font-bold mb-3">{t("A practical digital toolkit for Lean and Six Sigma teams.")}</h2>
          <p className="text-muted-foreground">
            {t("BEEDIGIT gives operational excellence teams the tools they need to improve daily factory performance using real-time data.")}
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {toolkit.map(({ icon: Icon, title, use }) => (
            <div key={title} className="rounded-lg border border-border bg-card p-4 hover-glow">
              <Icon className="h-5 w-5 text-primary mb-2" />
              <h3 className="font-heading font-bold mb-1">{t(title)}</h3>
              <p className="text-xs text-muted-foreground leading-relaxed"><span className="text-foreground/80 font-medium">{t("Use for:")}</span> {t(use)}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------- */
/* 5. Roles for OpEx teams                            */
/* -------------------------------------------------- */

const opexRoles = [
  { role: "Lean Manager / Operational Excellence Manager", body: "Run Kaizen projects based on real-time losses, not assumptions. Use OEE, downtime, quality, material flow and energy data to prioritize the biggest improvement opportunities." },
  { role: "Six Sigma Black Belt / Green Belt", body: "Use trusted production data for DMAIC projects. Define the problem, measure the baseline, analyze variation, implement countermeasures and control the result." },
  { role: "Production Manager", body: "Use daily OEE, output, downtime and job order data to run better shift meetings and improve execution discipline." },
  { role: "Maintenance Manager", body: "Use TPM dashboards, machine-hour data, downtime reasons, MTBF and MTTR to reduce breakdowns and improve asset reliability." },
  { role: "Quality Manager", body: "Connect defects, rework, scrap and quality checks to machines, orders, operators, batches and materials." },
  { role: "Plant Manager / COO", body: "Create a daily management system where every team works from the same real-time operational truth." },
];

export function OpExTeamsRoles() {
  const { t } = useLanguage();
  return (
    <section className="py-16 md:py-20 border-t border-border bg-[hsl(var(--section-alt))]">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mb-10">
          <p className="eyebrow mb-3">{t("Roles")}</p>
          <h2 className="text-3xl md:text-4xl font-bold mb-3">{t("Built for Operational Excellence, Lean and CI teams.")}</h2>
          <p className="text-muted-foreground">
            {t("BEEDIGIT helps improvement teams move from manual observation and delayed reporting to real-time factory intelligence.")}
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {opexRoles.map(({ role, body }) => (
            <div key={role} className="rounded-lg border border-border bg-card p-5">
              <div className="flex items-center gap-2 mb-2">
                <Users className="h-4 w-4 text-primary" />
                <h3 className="font-heading font-bold">{t(role)}</h3>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">{t(body)}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------- */
/* 6. Lean KPI Dashboards                             */
/* -------------------------------------------------- */

const kpis = [
  "OEE", "Availability", "Performance", "Quality rate", "First-pass yield",
  "Scrap rate", "Rework rate", "Downtime by reason", "Micro-stops", "MTBF",
  "MTTR", "Planned vs unplanned maintenance", "Changeover time", "Setup time",
  "Takt time vs cycle time", "Output vs target", "Plan vs actual", "WIP level",
  "Material waiting time", "Energy per unit / meter / kg / batch / order",
  "Idle energy loss", "Andon response time", "Action closure rate",
  "Cost of poor quality", "Bottleneck utilization",
];

export function LeanKPIDashboards() {
  const { t } = useLanguage();
  return (
    <section className="py-16 md:py-20 border-t border-border">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mb-8">
          <p className="eyebrow mb-3">{t("KPI dashboards")}</p>
          <h2 className="text-3xl md:text-4xl font-bold mb-3">{t("Lean KPIs updated automatically from the shop floor.")}</h2>
          <p className="text-muted-foreground">
            {t("Replace manual KPI collection with live dashboards based on machine, operator and process data.")}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {kpis.map((k) => (
            <span key={k} className="rounded-md border border-border bg-card px-3 py-1.5 text-sm text-foreground/85">{t(k)}</span>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------- */
/* 7. Daily Management System                         */
/* -------------------------------------------------- */

const dmsItems = [
  "Shift handover", "Tier 1 operator meetings", "Tier 2 supervisor meetings",
  "Tier 3 plant management meetings", "SQCDP boards: Safety, Quality, Cost, Delivery, People",
  "OEE review", "Downtime Pareto", "Open Andon calls", "Maintenance priorities",
  "Quality issues", "Material constraints", "Energy losses", "Kaizen action tracking",
];

export function DailyManagementSystem() {
  const { t } = useLanguage();
  return (
    <section className="py-16 md:py-20 border-t border-border bg-[hsl(var(--section-alt))]">
      <div className="container mx-auto px-4">
        <div className="grid lg:grid-cols-[1.1fr_1fr] gap-10 items-start">
          <div>
            <p className="eyebrow mb-3">{t("Daily Management System")}</p>
            <h2 className="text-3xl md:text-4xl font-bold mb-3">{t("Run daily factory meetings with facts, not opinions.")}</h2>
            <p className="text-muted-foreground mb-6">
              {t("BEEDIGIT supports a practical Daily Management System for production, maintenance, quality and management teams.")}
            </p>
            <Button size="lg" className="shadow-glow-primary" asChild>
              <Link to="/get-started">{t("Build Your Digital Daily Management System")} <ArrowRight className="ms-2 h-4 w-4" /></Link>
            </Button>
          </div>
          <div className="rounded-lg border border-border bg-card p-5">
            <p className="text-sm text-foreground/80 font-medium mb-3">{t("Use BEEDIGIT dashboards for:")}</p>
            <ul className="grid sm:grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
              {dmsItems.map((i) => (
                <li key={i} className="flex items-start gap-2 text-muted-foreground">
                  <span className="mt-1.5 inline-block h-1.5 w-1.5 rounded-full bg-primary shrink-0" />{t(i)}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------- */
/* 8. Six Sigma DMAIC                                 */
/* -------------------------------------------------- */

const dmaic = [
  { l: "D", title: "Define", body: "Use BEEDIGIT to identify the business problem: downtime, low OEE, quality defects, high scrap, energy waste, unstable output, long changeover, poor delivery." },
  { l: "M", title: "Measure", body: "Collect baseline data automatically: output, cycle time, downtime, defects, rework, scrap, energy, changeover time, maintenance losses." },
  { l: "A", title: "Analyze", body: "Use dashboards, Pareto charts, trend analysis and root cause tools to understand where losses come from." },
  { l: "I", title: "Improve", body: "Assign countermeasures, update standard work, improve maintenance, optimize job sequencing, reduce waiting and stabilize the process." },
  { l: "C", title: "Control", body: "Monitor the same KPIs after improvement to make sure the result is sustained and does not disappear after one week." },
];

export function SixSigmaDMAIC() {
  const { t } = useLanguage();
  return (
    <section className="py-16 md:py-20 border-t border-border">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mb-10">
          <p className="eyebrow mb-3">{t("Six Sigma")}</p>
          <h2 className="text-3xl md:text-4xl font-bold mb-3">{t("Support Six Sigma with real production data.")}</h2>
          <p className="text-muted-foreground">
            {t("BEEDIGIT gives Six Sigma teams the trusted measurement foundation needed for DMAIC projects.")}
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-3">
          {dmaic.map(({ l, title, body }) => (
            <div key={l} className="rounded-lg border border-border bg-card p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="inline-flex items-center justify-center h-8 w-8 rounded-md bg-primary/15 text-primary font-bold font-heading">{l}</span>
                <h3 className="font-heading font-bold">{t(title)}</h3>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">{t(body)}</p>
            </div>
          ))}
        </div>
        <p className="text-center text-sm text-muted-foreground mt-8 italic">
          {t("Six Sigma needs reliable measurement. BEEDIGIT makes measurement automatic.")}
        </p>
      </div>
    </section>
  );
}

/* -------------------------------------------------- */
/* 9. TPM Support                                     */
/* -------------------------------------------------- */

const tpmItems = [
  "Machine hour tracking", "Downtime reason tracking", "Breakdown history",
  "Maintenance requests from operators", "Preventive maintenance plans",
  "Corrective maintenance actions", "Autonomous maintenance checklists",
  "MTBF and MTTR dashboards", "Repeated failure analysis", "Maintenance Andon",
  "Spare parts links where configured", "OEE availability loss tracking",
];

export function TPMSupport() {
  const { t } = useLanguage();
  return (
    <section className="py-16 md:py-20 border-t border-border bg-[hsl(var(--section-alt))]">
      <div className="container mx-auto px-4">
        <div className="grid lg:grid-cols-[1fr_1.1fr] gap-10 items-start">
          <div>
            <p className="eyebrow mb-3">{t("TPM")}</p>
            <h2 className="text-3xl md:text-4xl font-bold mb-3">{t("Digital TPM for higher availability and fewer breakdowns.")}</h2>
            <p className="text-muted-foreground mb-4">
              {t("BEEDIGIT connects production losses with maintenance actions, helping teams improve asset reliability.")}
            </p>
            <p className="text-sm text-foreground/80">
              <span className="font-semibold text-foreground">{t("Business result:")}</span> {t("Less unplanned downtime, faster response, better machine reliability and higher useful output.")}
            </p>
          </div>
          <ul className="grid sm:grid-cols-2 gap-x-4 gap-y-2 text-sm">
            {tpmItems.map((i) => (
              <li key={i} className="flex items-start gap-2 rounded-md border border-border bg-card p-3">
                <Wrench className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                <span className="text-foreground/85">{t(i)}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------- */
/* 10. Standard Work & Digital 5S                     */
/* -------------------------------------------------- */

const stdWorkItems = [
  "Digital work instructions", "Job-specific checklists", "Quality gates",
  "Setup checklists", "Maintenance checklists", "Cleaning / inspection checklists",
  "QR-based identification", "Photo attachments", "Operator confirmations",
  "Deviation reporting", "Andon escalation", "Digital visual management boards",
];

export function StandardWorkDigital5S() {
  const { t } = useLanguage();
  return (
    <section className="py-16 md:py-20 border-t border-border">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mb-8">
          <p className="eyebrow mb-3">{t("Standard Work · 5S")}</p>
          <h2 className="text-3xl md:text-4xl font-bold mb-3">{t("Standard work, digital checklists and visual control.")}</h2>
          <p className="text-muted-foreground">
            {t("Lean improvement must become daily discipline. BEEDIGIT helps standardize how operators execute jobs, confirm output, report issues, complete quality checks and escalate problems. Software alone does not create 5S — BEEDIGIT is the digital support tool that makes standard work and 5S discipline visible and repeatable.")}
          </p>
        </div>
        <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {stdWorkItems.map((i) => (
            <div key={i} className="rounded-md border border-border bg-card p-3 text-sm flex items-start gap-2">
              <ClipboardCheck className="h-4 w-4 text-primary mt-0.5 shrink-0" />
              <span className="text-foreground/85">{t(i)}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------- */
/* 11. Lean Final CTA                                 */
/* -------------------------------------------------- */

export function LeanFinalCTA() {
  const { t } = useLanguage();
  return (
    <section className="py-20 md:py-24 border-t border-border bg-[hsl(var(--section-alt))]">
      <div className="container mx-auto px-4 max-w-3xl text-center">
        <p className="eyebrow mb-3">{t("Lean needs data. BEEDIGIT makes the data automatic.")}</p>
        <h2 className="text-3xl md:text-5xl font-bold mb-4">{t("Build your Lean factory on real-time trusted data.")}</h2>
        <p className="text-muted-foreground text-lg mb-8">
          {t("Start with one bottleneck, one line or one factory. BEEDIGIT will help your team see the losses, prioritize Kaizen actions, improve OEE, reduce downtime, control quality, optimize energy and scale continuous improvement across your manufacturing organization.")}
        </p>
        <div className="flex flex-wrap gap-3 justify-center">
          <Button size="lg" className="shadow-glow-primary" asChild>
            <Link to="/get-started">{t("Book Lean Factory Visibility Assessment")} <ArrowRight className="ms-2 h-4 w-4" /></Link>
          </Button>
          <Button size="lg" variant="outline" asChild>
            <Link to="/get-started">{t("Start Your First Kaizen Data Project")}</Link>
          </Button>
        </div>
        <p className="mt-8 text-xs text-muted-foreground flex flex-wrap justify-center gap-x-3 gap-y-1">
          <span>{t("From Gemba to dashboard.")}</span>
          <span>·</span>
          <span>{t("From downtime reason to Kaizen action.")}</span>
          <span>·</span>
          <span>{t("From machine signal to management decision.")}</span>
          <span>·</span>
          <span>{t("Run daily management with facts, not opinions.")}</span>
        </p>
      </div>
    </section>
  );
}